import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Visibility of the Project Plan's optional pinned columns, driven by the toolbar's
 * Columns panel and persisted per user on the server so the choice follows someone
 * across sessions, browsers and machines (IB-789).
 *
 * localStorage is still written on every toggle, but it only answers for someone who
 * has no preference row yet — carrying the choice of anyone who made it before this
 * became server-backed. It is not a general recency fallback: `load()` prefers the
 * server value, so a failed save is invisible until the next full page load, which
 * then shows the last value the server did accept. Doing better would mean comparing
 * timestamps, which this preference does not justify.
 *
 * The name column and the timeline are structural and never appear here.
 *
 * Cost starts on only where the organisation actually costs its plan (the event's
 * Workforce module, handed over as `window.projectPlanUsesCosting`) — an empty money
 * column is worse than no column. Source starts open only on a wide viewport: all five
 * pinned columns together leave a narrow screen with barely any timeline, and Source is
 * both the widest optional column and the least often needed on first look. Both are
 * no-stored-preference defaults only — a stored choice always wins over either.
 */
const SOURCE_DEFAULT_MIN_VIEWPORT = 1440;

const STORAGE_KEY = 'project-plan-columns-v1';

const PREFERENCE_MODULE = 'project_plan_columns';

export const COLUMN_KEYS = ['owner', 'source', 'people', 'cost', 'float'];

// Columns added after preferences were first stored. A stored map without one is still usable,
// and the column takes its default.
const OPTIONAL_COLUMN_KEYS = ['float'];

const defaultsFor = () => ({
    owner: true,
    // Evaluated once, on mount: a default that moved with the window would open or close a
    // column underneath someone mid-task, which is worse than the layout it would be fixing.
    source: window.innerWidth >= SOURCE_DEFAULT_MIN_VIEWPORT,
    people: true,
    cost: !!window.projectPlanUsesCosting,
    float: false,
});

/**
 * A candidate visibility map is only usable if it names every column with a boolean —
 * a partial or malformed one would leave columns `undefined`, which reads as "hidden"
 * without anyone having hidden it. Returns the healed map, or null to say "unusable,
 * fall through to the next source" (mirrors `useREColumns`).
 *
 * Applied to the server value as well as the stored one: a preference row written by an
 * older shape of this panel is no more trustworthy than a hand-edited localStorage key.
 */
const validate = (candidate) => {
    if (!candidate || typeof candidate !== 'object') return null;

    const healed = {};
    const complete = COLUMN_KEYS.every((key) => {
        if (OPTIONAL_COLUMN_KEYS.includes(key) && candidate[key] === undefined) {
            healed[key] = false;
            return true;
        }

        if (typeof candidate[key] !== 'boolean') {
            return false;
        }
        healed[key] = candidate[key];
        return true;
    });

    return complete ? healed : null;
};

const loadFromStorage = () => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;

        return validate(JSON.parse(raw));
    } catch (e) {
        return null;
    }
};

const saveToStorage = (value) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch (e) {
        // Ignore quota / privacy-mode failures — visibility still works for the session.
    }
};

/**
 * Fire-and-forget write to the per-user preference store. Never awaited by the toggle:
 * showing or hiding a column is an instant, local thing, and making it wait on a round
 * trip would be a visible regression for the sake of a preference. A failure costs the
 * user nothing for the rest of the session — state and the window copy already moved —
 * but it is not repaired later: the next full page load reads the server value back.
 */
async function persistColumns(newValue) {
    try {
        const response = await fetch('/preferences', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content ?? '',
                Accept: 'application/json',
            },
            body: JSON.stringify({ module: PREFERENCE_MODULE, value: newValue }),
        });

        // `fetch` only rejects on a network-level failure, so the likeliest way this
        // write fails — an expired session answering 419, or a redirect to login —
        // arrives as a perfectly resolved response. Without this the fallback is
        // still correct but completely silent, and cross-device carry-over stops
        // working with nothing anywhere to say so.
        if (!response.ok) {
            console.warn('[useProjectPlanColumns] Column visibility was not saved', response.status);
        }
    } catch (e) {
        console.warn('[useProjectPlanColumns] Failed to persist column visibility', e);
    }
}

/**
 * Server preference → localStorage → defaults. The server wins because it is the only
 * copy that survives a different browser; localStorage still answers for anyone who
 * chose their columns before this became server-backed, and for the window between a
 * toggle and its (possibly failed) request.
 */
const load = () => validate(window.projectPlanColumnsPreference) ?? loadFromStorage() ?? defaultsFor();

export default function useProjectPlanColumns() {
    const [columns, setColumns] = useState(load);
    const migrationAttempted = useRef(false);

    // One-time carry-over: someone who already picked their columns under the
    // localStorage-only version has no server row yet, and would otherwise keep that
    // choice only until they next opened the plan somewhere else. Adopt it as their
    // server value on first mount so nobody's columns silently change.
    useEffect(() => {
        if (migrationAttempted.current) return;
        migrationAttempted.current = true;

        if (validate(window.projectPlanColumnsPreference)) return;

        const stored = loadFromStorage();
        if (!stored) return;

        // Keep the page's own copy of the preference in step with what the server is
        // now being told, so a second mount does not repeat the write.
        window.projectPlanColumnsPreference = stored;

        persistColumns(stored);
    }, []);

    const toggle = useCallback((key) => {
        setColumns((prev) => {
            const next = { ...prev, [key]: !prev[key] };

            // Optimistic: state and the local fallback are updated now, the server is
            // told afterwards and its answer is never waited on (see persistColumns).
            saveToStorage(next);

            // The page's own copy has to move too. `load()` reads it before anything
            // else, and the plan is unmounted whenever the Project Management tabs
            // switch away from it (ProjectPlanIndex renders it behind
            // `activeTab === PROJECT_PLAN_CREATE`), so leaving this at its page-load
            // value would silently undo the toggle on the way back to the tab.
            window.projectPlanColumnsPreference = next;

            persistColumns(next);

            return next;
        });
    }, []);

    const definitions = useMemo(
        () => [
            { key: 'owner', label: $t('Owner') },
            { key: 'people', label: $t('People') },
            { key: 'cost', label: $t('Cost'), hint: $t('Not all organisations use this') },
            { key: 'source', label: $t('Source'), masterOnly: true },
            { key: 'float', label: $t('Float') },
        ],
        [],
    );

    return { columns, toggle, definitions };
}
